import httpClient from './httpClient';

const getSalesOrders = async (page = 1, pageSize = 10, filters = {}) => {
  const params = {
    page,
    page_size: pageSize,
    // Remove undefined values to clean up the request
    ...(filters.sales_order_id && { sales_order_id: filters.sales_order_id }),
    ...(filters.client_name && { client_name: filters.client_name }),
    ...(filters.status && { status: filters.status }),
    ...(filters.price_column && { price_column: filters.price_column }),
    ...(filters.start_date && { start_date: filters.start_date }),
    ...(filters.end_date && { end_date: filters.end_date }),
    // Add sorting parameters
    ...(filters.sort_by && { sort_by: filters.sort_by }),
    ...(filters.sort_order && { sort_order: filters.sort_order }),
    // Add client_type filter for domestic/import filtering
    ...(filters.client_type && { client_type: filters.client_type }),
  };

  try {
    const response = await httpClient.get('/api/sales', { params });
    // Backend now returns proper JSON, so we can directly use response.data
    return response.data;
  } catch (error) {
    console.error('Error fetching sales orders:', error);
    throw error;
  }
};

const createSalesOrder = async (orderData) => {
  try {
    const response = await httpClient.post('/api/sales', orderData);
    return response.data;
  } catch (error) {
    console.error('Error creating sales order:', error);
    throw error;
  }
};

const getSalesOrder = async (orderId) => {
  try {
    const response = await httpClient.get(`/api/sales/${orderId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching sales order:', error);
    throw error;
  }
};

const updateSalesOrder = async (orderId, orderData) => {
  try {
    const response = await httpClient.put(`/api/sales/${orderId}`, orderData);
    return response.data;
  } catch (error) {
    console.error('Error updating sales order:', error);
    throw error;
  }
};

const confirmSalesOrder = async (orderId) => {
  try {
    const response = await httpClient.post(`/api/sales/${orderId}/confirm`, {});
    return response.data;
  } catch (error) {
    console.error('Error confirming sales order:', error);
    throw error;
  }
};

const shipOrder = async (orderId) => {
  try {
    const response = await httpClient.post(`/api/sales/${orderId}/ship`, {});
    return response.data;
  } catch (error) {
    console.error('Error shipping order:', error);
    throw error;
  }
};

const shipProductWithFrozen = async (orderId, lineItemId) => {
  try {
    const response = await httpClient.post(`/api/sales/${orderId}/ship-product`, {
      line_item_id: lineItemId
    });
    return response.data;
  } catch (error) {
    console.error('Error shipping product with frozen stock:', error);
    throw error;
  }
};

const invoiceOrder = async (orderId) => {
  try {
    const response = await httpClient.post(`/api/sales/${orderId}/invoice`, {});
    return response.data;
  } catch (error) {
    console.error('Error marking order as invoiced:', error);
    throw error;
  }
};

const cancelSalesOrder = async (orderId) => {
  try {
    const response = await httpClient.delete(`/api/sales/${orderId}`);
    return response.data;
  } catch (error) {
    console.error('Error cancelling sales order:', error);
    throw error;
  }
};

const deleteSalesOrder = async (orderId) => {
  try {
    const response = await httpClient.delete(`/api/sales/${orderId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting sales order:', error);
    throw error;
  }
};

const getSalesStats = async () => {
  try {
    const response = await httpClient.get('/api/sales/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching sales stats:', error);
    throw error;
  }
};

// SO-PO Conversion Functions
const convertSalesOrderToPurchaseOrders = async (salesOrderId, conversionData) => {
  try {
    const response = await httpClient.post(
      `/api/sales/${salesOrderId}/convert-to-purchase-orders`, 
      conversionData
    );
    return response.data;
  } catch (error) {
    console.error('Error converting sales order to purchase orders:', error);
    throw error;
  }
};

// DEPRECATED: Use getExecutionProgress instead
// Kept for backward compatibility, will be removed in future version
const getSalesOrderProcurementStatus = async (salesOrderId) => {
  console.warn('getSalesOrderProcurementStatus is deprecated, use getExecutionProgress instead');
  try {
    const response = await httpClient.get(`/api/sales/${salesOrderId}/procurement-status`);
    return response.data;
  } catch (error) {
    console.error('Error fetching sales order procurement status:', error);
    throw error;
  }
};

const getLinkedPurchaseOrders = async (salesOrderId) => {
  try {
    const response = await httpClient.get(`/api/sales/${salesOrderId}/linked-purchase-orders`);
    return response.data;
  } catch (error) {
    console.error('Error fetching linked purchase orders:', error);
    throw error;
  }
};

// New SO-PO Splitting API methods
const convertSoToPoWithMapping = async (salesOrderId, conversionData) => {
  try {
    const response = await httpClient.post(
      `/api/sales-orders/${salesOrderId}/convert-to-purchase`,
      conversionData
    );
    return response.data;
  } catch (error) {
    console.error('Error converting SO to PO with mapping:', error);
    throw error;
  }
};

// DEPRECATED: Use getExecutionProgress instead
// Kept for backward compatibility, will be removed in future version
const getSalesOrderPurchaseMappings = async (salesOrderId, page = 1, pageSize = 50) => {
  console.warn('getSalesOrderPurchaseMappings is deprecated, use getExecutionProgress instead');
  try {
    const response = await httpClient.get(
      `/api/sales-orders/${salesOrderId}/purchase-mappings?page=${page}&page_size=${pageSize}`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching SO purchase mappings:', error);
    throw error;
  }
};

const getPurchaseOrderSalesMappings = async (purchaseOrderId) => {
  try {
    const response = await httpClient.get(`/api/purchase-orders/${purchaseOrderId}/sales-mappings`);
    return response.data;
  } catch (error) {
    console.error('Error fetching PO sales mappings:', error);
    throw error;
  }
};

// DEPRECATED: Use getExecutionProgress instead
// Kept for backward compatibility, will be removed in future version
const getSalesOrderProcurementRollups = async (salesOrderId) => {
  console.warn('getSalesOrderProcurementRollups is deprecated, use getExecutionProgress instead');
  try {
    const response = await httpClient.get(`/api/sales-orders/${salesOrderId}/procurement-rollups`);
    return response.data;
  } catch (error) {
    console.error('Error fetching SO procurement rollups:', error);
    throw error;
  }
};

/**
 * Preview product sync changes (NO database modifications)
 * @param {string} orderId - Sales order MongoDB ObjectId
 * @param {Object} options - Optional sync configuration
 * @param {string[]} options.product_ids - Array of product IDs to preview sync (optional, defaults to all)
 * @param {string[]} options.sync_fields - Fields to sync (optional, defaults to ['name', 'specification', 'category', 'client_ref_number'])
 * @returns {Promise<Object>} Preview result with changes, affected_count, total_products
 */
const previewSyncSalesOrderProducts = async (orderId, options = {}) => {
  try {
    const response = await httpClient.post(`/api/sales/${orderId}/preview-sync-products`, options);
    return response.data;
  } catch (error) {
    console.error('Error previewing sales order product sync:', error);
    throw error;
  }
};

/**
 * Sync product information from products collection to sales order
 * @param {string} orderId - Sales order MongoDB ObjectId
 * @param {Object} options - Optional sync configuration
 * @param {string[]} options.product_ids - Array of product IDs to sync (optional, defaults to all)
 * @param {string[]} options.sync_fields - Fields to sync (optional, defaults to ['name', 'specification', 'category', 'client_ref_number'])
 * @returns {Promise<Object>} Sync result with changes
 */
const syncSalesOrderProducts = async (orderId, options = {}) => {
  try {
    const response = await httpClient.post(`/api/sales/${orderId}/sync-products`, options);
    return response.data;
  } catch (error) {
    console.error('Error syncing sales order products:', error);
    throw error;
  }
};

/**
 * Get comprehensive fulfillment status including inventory availability
 * @param {string} salesOrderId - Sales order MongoDB ObjectId
 * @returns {Promise<Object>} Fulfillment status with inventory, procurement, and warehouse data
 */
const getSalesOrderFulfillmentStatus = async (salesOrderId) => {
  try {
    const response = await httpClient.get(`/api/sales/${salesOrderId}/fulfillment-status`);
    return response.data;
  } catch (error) {
    console.error('Error fetching fulfillment status:', error);
    throw error;
  }
};

/**
 * Link existing purchase orders to a sales order
 * Creates SO-PO mappings for manual allocation of existing POs to an SO.
 * Supports partial allocation (partial products and quantities).
 *
 * @param {string} salesOrderId - Sales order MongoDB ObjectId
 * @param {Array} poAllocations - Array of PO allocations with product mappings
 * @param {string} poAllocations[].po_id - Purchase order ID
 * @param {Array} poAllocations[].allocations - Product allocation mappings
 * @param {string} poAllocations[].allocations[].so_product_id - SO product code
 * @param {string} poAllocations[].allocations[].po_product_id - PO product code
 * @param {number} poAllocations[].allocations[].allocated_qty - Quantity to allocate
 * @returns {Promise<Object>} Result with mappings created count and details
 */
const linkPurchaseOrdersToSalesOrder = async (salesOrderId, poAllocations) => {
  try {
    const response = await httpClient.post(
      `/api/sales-orders/${salesOrderId}/link-purchase-orders`,
      { po_allocations: poAllocations }
    );
    return response.data;
  } catch (error) {
    console.error('Error linking purchase orders to sales order:', error);
    throw error;
  }
};

const linkProductionOrdersToSalesOrder = async (salesOrderId, prdAllocations) => {
  try {
    const response = await httpClient.post(
      `/api/sales-orders/${salesOrderId}/link-production-orders`,
      { prd_allocations: prdAllocations }
    );
    return response.data;
  } catch (error) {
    console.error('Error linking production orders to sales order:', error);
    throw error;
  }
};

/**
 * Get execution progress with accurate timestamp-based calculations
 * This unified endpoint replaces 4 separate API calls with a single efficient call.
 *
 * Key improvements:
 * - Stock calculation based on movements BEFORE SO creation (not current stock)
 * - Received quantity only counts movements AFTER SO creation
 * - Shipped quantity only counts movements AFTER SO creation
 * - Single API call instead of 4 redundant calls
 *
 * @param {string} salesOrderId - Sales order MongoDB ObjectId
 * @returns {Promise<Object>} Execution progress with accurate historical calculations
 */
const getExecutionProgress = async (salesOrderId) => {
  try {
    const response = await httpClient.get(`/api/sales/${salesOrderId}/execution-progress`);
    return response.data;
  } catch (error) {
    console.error('Error fetching execution progress:', error);
    throw error;
  }
};

const salesService = {
  getSalesOrders,
  createSalesOrder,
  getSalesOrder,
  updateSalesOrder,
  confirmSalesOrder,
  shipOrder,
  shipProductWithFrozen,
  invoiceOrder,
  cancelSalesOrder,
  deleteSalesOrder,
  getSalesStats,
  // SO-PO Conversion (Legacy)
  convertSalesOrderToPurchaseOrders,
  getSalesOrderProcurementStatus,
  getLinkedPurchaseOrders,
  // New SO-PO Splitting with Individual Mappings
  convertSoToPoWithMapping,
  getSalesOrderPurchaseMappings,
  getPurchaseOrderSalesMappings,
  getSalesOrderProcurementRollups,
  linkPurchaseOrdersToSalesOrder,
  linkProductionOrdersToSalesOrder,
  // Product sync
  previewSyncSalesOrderProducts,
  syncSalesOrderProducts,
  // Fulfillment status with inventory
  getSalesOrderFulfillmentStatus,
  // New unified execution progress endpoint
  getExecutionProgress,

  // Unified Conversion (Purchase + Production)
  unifiedConversion: async (salesOrderId, conversionData) => {
    try {
      const response = await httpClient.post(
        `/api/sales-orders/${salesOrderId}/unified-conversion`,
        conversionData
      );
      return response.data;
    } catch (error) {
      console.error('Error in unified conversion:', error);
      throw error;
    }
  },

  getShortageAnalysis: async (salesOrderId) => {
    try {
      const response = await httpClient.get(`/api/sales-orders/${salesOrderId}/shortage-analysis`);
      return response.data;
    } catch (error) {
      console.error('Error fetching shortage analysis:', error);
      throw error;
    }
  },
};

export default salesService;
